import {gqlFetch, throwUserErrors, fetchProductConfiguration} from './product-configurator';
import {GROUP_TYPE, emptySwitcher, switcherFromMetaobject, switcherFields, validateSwitcher, objectFingerprint, sameSwitcher, groupMembers, reconcileSwitcher} from '../product-switcher.js';

export const objectFields = 'id handle type fields { key value reference { ... on Product { id title handle } } references(first: 100) { nodes { ... on Product { id title handle } } pageInfo { hasNextPage endCursor } } }';

export async function fetchSwitcherProduct(productId: string) {
  return fetchProductConfiguration(productId);
}

export async function fetchSnapshot(id: string) {
  const json = await gqlFetch(`#graphql
    query SwitcherSnapshot($id: ID!) { metaobject(id: $id) { ${objectFields} } }
  `, {id});
  if (!json.data.metaobject) throw new Error('The group is missing or inaccessible. Choose another group.');
  return json.data.metaobject;
}

export function groupHasMember(group: any, productId: string) {
  const field = group.fields?.find((item: any) => item.key === 'members');
  if (field?.value || group.fields?.some((item: any) => item.key === 'configuration' && item.value)) return JSON.parse(field?.value || '[]').includes(productId);
  return switcherFromMetaobject(group).products.some((item: any) => item.productId === productId);
}

export async function fetchMembershipGroups(productId: string) {
  const groups: any[] = [];
  let after = null;
  do {
    // Paginate the type and inspect native references: no filterable-definition
    // assumption, no store-wide product scan, and no truncated first-page match.
    const json = await gqlFetch(`#graphql
      query SwitcherMembershipGroups($after: String) {
        metaobjects(type: "evp_configurator_product_group", first: 100, after: $after) {
          nodes { ${objectFields} }
          pageInfo { hasNextPage endCursor }
        }
      }
    `, {after});
    groups.push(...json.data.metaobjects.nodes.filter((group: any) =>
      !group.handle.startsWith('evp-switcher-draft-') &&
      !switcherFromMetaobject(group).detached && groupHasMember(group, productId)));
    after = json.data.metaobjects.pageInfo.hasNextPage ? json.data.metaobjects.pageInfo.endCursor : null;
  } while (after);
  return groups;
}

export async function hydrate(value: any) {
  const ids = value.products.map((item: any) => item.productId).filter(Boolean);
  if (!ids.length) return value;
  const json = await gqlFetch(`#graphql
    query SwitcherMembers($ids: [ID!]!) { nodes(ids: $ids) { ... on Product { id title handle } } }
  `, {ids});
  return {...value, products: value.products.map((slot: any) => {
    const product = json.data.nodes.find((item: any) => item?.id === slot.productId);
    return {...slot, title: product?.title || '', handle: product?.handle || ''};
  })};
}


export async function sessionForGroup(product: any, group: any, configuration: any) {
  const stored = configuration.productSwitcherDraft;
  const draft = group && stored?.baseHandle === group.handle ? stored : null;
  const value = await hydrate(draft?.value || (group ? switcherFromMetaobject(group) : emptySwitcher(product)));
  value.products = [...value.products, ...emptySwitcher().products].slice(0, 5);
  const baseFingerprint = draft ? draft.baseFingerprint : objectFingerprint(group);
  return {productId: product.id, product, published: group, selected: group,
    selectedHandle: configuration.productGroupHandle || '', savedHandle: configuration.productGroupHandle || '',
    active: Boolean(group), baseId: group?.id || null, baseFingerprint, value, saved: structuredClone(value), draft,
    stale: Boolean(group && draft && objectFingerprint(group) !== baseFingerprint && !sameSwitcher(value, switcherFromMetaobject(group)))};
}

export async function fetchSwitcherSession(productId: string, ignoreDraft = false, loadedProduct: any = null) {
  const product = loadedProduct || await fetchProductConfiguration(productId);
  const configuration = ignoreDraft ? product.publishedConfiguration || product.configuration : product.configuration;
  const handle = configuration.productGroupHandle || '';
  const group = handle ? await groupByHandle(handle) : null;
  if (handle && !group) throw new Error('The selected group handle is missing or inaccessible. Restore it before continuing.');
  if (group && switcherFromMetaobject(group).detached) throw new Error('The selected group is detached.');
  return sessionForGroup(product, group, configuration);
}

export function switcherDirty(session: any) {
  return Boolean(session && (session.needsSave || session.selectedHandle !== session.savedHandle || session.published && !sameSwitcher(session.value, session.saved)));
}

export function switcherUnpublished(session: any) {
  return Boolean(session?.draft && session.published && !sameSwitcher(session.draft.value, switcherFromMetaobject(session.published)));
}

function draftEnvelope(session: any, value = session.value) {
  return {schemaVersion: 1, active: session.active, baseHandle: session.selectedHandle,
    baseId: session.baseId, baseFingerprint: session.baseFingerprint,
    value: {...value, groupId: value.groupId || crypto.randomUUID()}};
}

export function prepareSwitcherSave(session: any, configuration: any) {
  if (!session) throw new Error('Wait for the group settings to load.');
  const next = structuredClone(configuration);
  next.productGroupHandle = session.selectedHandle || '';
  delete next.productSwitcherDraft;
  if (session.published && (session.draft || !sameSwitcher(session.value, switcherFromMetaobject(session.published)))) {
    validateSwitcher(session.value);
    next.productSwitcherDraft = draftEnvelope(session);
  }
  return next;
}

export async function prepareSwitcherReset(session: any) {
  return fetchSwitcherSession(session.productId, true);
}

export async function writeGroup(value: any, handle: string, create = false, fields = switcherFields(value, false, crypto.randomUUID())) {
  if (create) {
    // Never let a racing initial handle allocation overwrite another group.
    const json = await gqlFetch(`#graphql
      mutation CreateSwitcherGroup($metaobject: MetaobjectCreateInput!) {
        metaobjectCreate(metaobject: $metaobject) {
          metaobject { ${objectFields} }
          userErrors { field message code }
        }
      }
    `, {metaobject: {type: GROUP_TYPE, handle, fields}});
    throwUserErrors(json.data.metaobjectCreate);
    return json.data.metaobjectCreate.metaobject;
  }
  const json = await gqlFetch(`#graphql
    mutation WriteSwitcherGroup($handle: MetaobjectHandleInput!, $metaobject: MetaobjectUpsertInput!) {
      metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
        metaobject { ${objectFields} }
        userErrors { field message code }
      }
    }
  `, {handle: {type: GROUP_TYPE, handle}, metaobject: {fields}});
  throwUserErrors(json.data.metaobjectUpsert);
  return json.data.metaobjectUpsert.metaobject;
}

export async function groupByHandle(handle: string) {
  const json = await gqlFetch(`#graphql
    query SwitcherHandle($handle: MetaobjectHandleInput!) {
      metaobjectByHandle(handle: $handle) { ${objectFields} }
    }
  `, {handle: {type: GROUP_TYPE, handle}});
  return json.data.metaobjectByHandle;
}


export async function prepareSwitcherPublication(session: any) {
  if (!session?.published) return {sharedChanged: false, configuration: session?.product?.savedDraft};
  if (switcherDirty(session)) throw new Error('Save your draft before publishing.');
  let group = await groupByHandle(session.selectedHandle);
  if (!group || group.id !== session.published.id || !groupHasMember(group, session.productId)) throw new Error('The selected group is missing or no longer contains this product.');
  const value = await hydrate(session.saved);
  if (!session.draft || sameSwitcher(value, switcherFromMetaobject(group))) return {sharedChanged: false};
  const memberIds = validateSwitcher(value, true);
  if (!memberIds.includes(session.productId) || value.products.some((slot: any) => slot.productId && !slot.handle)) throw new Error('A group member is missing or inaccessible.');
  if (objectFingerprint(group) !== session.baseFingerprint) throw new Error('The shared group changed since this draft was saved. Reset the switcher before publishing.');
  if (JSON.stringify([...memberIds].sort()) !== JSON.stringify(groupMembers(group).map((member: any) => member.productId).sort())) throw new Error('Group membership changed. Reset the switcher draft before publishing.');
  const existing = JSON.parse(group.fields.find((field: any) => field.key === 'configuration')?.value || '{}');
  const settings = JSON.parse(switcherFields(value, true, crypto.randomUUID())[2].value);
  settings.products = settings.products.map((item: any) => ({...existing.products?.find((member: any) => member.productId === item.productId), ...item}));
  group = await writeGroup(value, group.handle, false, [{key: 'configuration', value: JSON.stringify({...existing, ...settings})}]);
  return {sharedChanged: true, draft: {...draftEnvelope(session, value), baseFingerprint: objectFingerprint(group)}};
}

export async function publishSwitcherToGroup(session: any) {
  if (!session?.published || !session.active) throw new Error('Select a group before publishing the switcher.');
  if (session.stale) throw new Error('The shared group changed. Reload before publishing.');
  validateSwitcher(session.value, true);
  await prepareSwitcherPublication({...session, needsSave: false, savedHandle: session.selectedHandle,
    saved: structuredClone(session.value), draft: draftEnvelope(session)});
  const group = await groupByHandle(session.selectedHandle);
  const fresh = await sessionForGroup(session.product, group, {productGroupHandle: session.selectedHandle});
  return {...fresh, savedHandle: session.savedHandle, needsSave: true};
}

export function refreshAfterGroupSave(previous: any, fresh: any) {
  if (!fresh.published || previous.published?.id !== fresh.published.id || previous.stale) return fresh;
  const value = reconcileSwitcher(previous.value, groupMembers(fresh.published));
  return {...fresh, value, saved: previous.saved, savedHandle: previous.savedHandle,
    needsSave: switcherDirty(previous) || Boolean(previous.draft),
    baseFingerprint: objectFingerprint(fresh.published), stale: false};
}
